import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Divider,
  FormControlLabel,
  IconButton,
  InputLabel,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import useDraftState from '@/hooks/useDraftState';
import useStyles from '@/pages/Administration/AdministrationGeneral/AdministrationGeneral.styles';

import type { AutoCloseConfigurationProps } from './AutoCloseConfiguration.types';

const AutoCloseConfiguration = (props: AutoCloseConfigurationProps) => {
  const { classes } = useStyles();
  const { handleSave, orgInfo } = props;
  const [editing, isEditing] = useState<boolean>(false);
  const [updatedOrgInfo, updateDraft, resetDraft] = useDraftState(orgInfo);

  const handleEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateDraft({ auto_close_enabled: event.target.checked });
  };

  const handlePeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    // Clamp value between 7 and 21
    updateDraft({
      auto_close_period_days: Math.max(7, Math.min(21, value || 7))
    });
  };

  return (
    <LargeCard
      title="Ticket Auto-Close"
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {editing ? (
            <Stack direction="row">
              <IconButton
                onClick={() => {
                  isEditing(!editing);
                  handleSave(updatedOrgInfo);
                }}
              >
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton
                onClick={() => {
                  isEditing(!editing);
                  resetDraft();
                }}
              >
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => isEditing(!editing)}>
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      {/* Auto-Close Enabled Section */}
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Automatic Ticket Closure
      </Typography>
      <Typography className={classes.subText} variant="body2">
        When enabled, all tickets with no activity within the selected timeframe
        (inactivity period) will be closed.
      </Typography>
      {editing && (
        <Alert severity="info" sx={{ my: 1 }}>
          Reminder emails will be sent at 2, 4, and 6 days before auto-closure.
          Individual tickets can be excluded from auto-closure when needed.
        </Alert>
      )}
      <FormControlLabel
        control={
          <Switch
            checked={updatedOrgInfo.auto_close_enabled || false}
            onChange={handleEnabledChange}
            disabled={!editing}
            color="primary"
            name="autoCloseEnabled"
          />
        }
        label="Enable automatic ticket closure"
      />

      <Divider sx={{ my: 1.5 }} />

      {/* Auto-Close Period Section */}
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Inactivity Period
      </Typography>
      <Typography className={classes.subText} variant="body2">
        Set the number of days of inactivity before a ticket is automatically
        closed (between 7 and 21 days). This applies to all tickets unless
        individually disabled.
      </Typography>
      <InputLabel sx={{ mt: '1rem' }} id="autoClosePeriodLabel">
        Number of days
      </InputLabel>
      <TextField
        className={classes.textField}
        onChange={handlePeriodChange}
        aria-labelledby="autoClosePeriod"
        name="autoClosePeriod"
        variant="outlined"
        disabled={!editing}
        type="number"
        value={updatedOrgInfo.auto_close_period_days}
        slotProps={{
          htmlInput: {
            min: 7,
            max: 21
          }
        }}
      />
    </LargeCard>
  );
};

export default AutoCloseConfiguration;
